import { useState, useEffect } from "react";
import { useQuotes } from "@/hooks/use-quotes";
import { useEnhancedQuotes } from "@/hooks/use-enhanced-quotes";
import { useApplications } from "@/hooks/use-applications";
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardDescription, 
  CardContent 
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { FileText, ExternalLink, Clock, AlertCircle, Calculator, TrendingUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatCurrency } from "@/components/peo/dashboard/utils/quoteUtils";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { QuoteComparisonMatrix } from "@/components/quotes/comparison/QuoteComparisonMatrix";
import { BaselineComparisonSetup } from "@/components/quotes/comparison/BaselineComparisonSetup";
import { QuoteComparisonSkeleton, QuoteListSkeleton } from "@/components/quotes/LoadingSkeleton";
import { QuoteErrorBoundary } from "@/components/quotes/ErrorBoundary";
import { MobileQuoteCard } from "@/components/quotes/MobileQuoteCard";
import { TourGuide, useTourGuide } from "@/components/quotes/TourGuide";
import { useIsMobile } from "@/hooks/use-mobile";

export const ReceivedQuotes = () => {
  const { quotes, isLoading, error, respondToQuote } = useQuotes();
  const { applications } = useApplications();
  
  // Find the most recent submitted/approved/completed application
  const eligibleApplication = applications?.find(app => 
    app.status === 'submitted' || app.status === 'approved' || app.status === 'completed'
  );
  
  const { 
    quotes: enhancedQuotes, 
    baseline, 
    isLoading: enhancedLoading,
    saveBaseline 
  } = useEnhancedQuotes(eligibleApplication?.id);
  const [activeTab, setActiveTab] = useState("quotes");
  const [showComparison, setShowComparison] = useState(false);
  const { toast } = useToast();
  const isMobile = useIsMobile();
  
  // Tour guide for enhanced features
  const { isActive: isTourActive, completeTour, skipTour } = useTourGuide("enhanced-quotes");
  
  const tourSteps = [
    {
      id: "compare",
      title: "Compare All Quotes",
      content: "Use the Compare All button to see detailed side-by-side analysis of all your quotes with savings calculations and implementation timelines."
    },
    {
      id: "baseline",
      title: "Set Your Baseline",
      content: "Set up your current costs as a baseline to see potential savings from each PEO quote. This helps you make informed decisions."
    },
    {
      id: "metrics",
      title: "Enhanced Metrics",
      content: "View detailed metrics including service scores, implementation timelines, and recommendation scores for each provider."
    }
  ];
  
  if (isLoading || enhancedLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>PEO Quotes</CardTitle>
          <CardDescription>Loading your quotes...</CardDescription>
        </CardHeader>
        <CardContent>
          <QuoteListSkeleton />
        </CardContent>
      </Card>
    );
  }
  
  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>PEO Quotes</CardTitle>
          <CardDescription>Error loading quotes</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <p>There was a problem loading your quotes.</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (!quotes || quotes.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>PEO Quotes</CardTitle>
          <CardDescription>No quotes available yet</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-center text-muted-foreground py-6">
            You haven't received any quotes for your applications yet. Once PEO providers review your application and submit quotes, they will appear here.
          </p>
        </CardContent>
      </Card>
    );
  }
  
  const getQuotesByStatus = (status: string) => {
    if (status === "all") return quotes;
    return quotes.filter(quote => quote.status === status);
  };
  
  const displayQuotes = getQuotesByStatus(activeTab);
  
  const handleAction = (quote: any, action: 'interested' | 'accepted' | 'declined') => {
    const actionText = action === 'interested' ? 'Marked as interested' : 
                     action === 'accepted' ? 'Accepted' : 'Declined';
                     
    respondToQuote({
      quoteId: quote.id,
      status: action,
      notes: `User ${actionText} the quote via dashboard`
    });
    
    toast({
      title: `Quote ${actionText}`,
      description: action === 'accepted' ? 
        "You've accepted this quote. The PEO provider will be notified." :
        action === 'declined' ? 
        "You've declined this quote. The PEO provider will be notified." :
        "You've marked interest in this quote. The PEO provider will be notified.",
    });
  };

  const handleViewDetails = (quoteId: string) => {
    setShowComparison(true);
  };

  const handleRespond = (quoteId: string, action: 'accept' | 'decline' | 'interested') => {
    const quote = quotes?.find(q => q.id === quoteId);
    if (quote) {
      const mappedAction = action === 'accept' ? 'accepted' : action === 'decline' ? 'declined' : action;
      handleAction(quote, mappedAction);
    }
  };

  const handleBaselineSet = (newBaseline: any) => {
    saveBaseline(newBaseline);
  };

  // Use enhanced quotes if available, fallback to regular quotes
  const quotesToDisplay = enhancedQuotes?.length > 0 ? enhancedQuotes : quotes || [];
  const hasEnhancedData = enhancedQuotes?.length > 0;
  
  // Get comparison metrics using the hook function with proper async handling
  const { getComparisonMetrics } = useEnhancedQuotes(eligibleApplication?.id);
  const [comparisonMetrics, setComparisonMetrics] = useState<any[]>([]);
  const [isCalculating, setIsCalculating] = useState(false);
  
  useEffect(() => {
    if (hasEnhancedData) {
      setIsCalculating(true);
      getComparisonMetrics()
        .then((metrics) => {
          console.log('Received comparison metrics:', metrics);
          setComparisonMetrics(metrics);
        })
        .catch((error) => {
          console.error('Error fetching comparison metrics:', error);
          setComparisonMetrics([]);
        })
        .finally(() => {
          setIsCalculating(false);
        });
    } else {
      setComparisonMetrics([]);
    }
  }, [hasEnhancedData, getComparisonMetrics, quotes?.length]);

  return (
    <QuoteErrorBoundary>
      <div className="space-y-6">
        <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            PEO Quotes
            <div className="flex gap-2">
              {hasEnhancedData && (
                <Button
                  variant={showComparison ? "default" : "outline"}
                  onClick={() => setShowComparison(!showComparison)}
                  className="gap-2"
                >
                  <TrendingUp className="h-4 w-4" />
                  {showComparison ? "Simple View" : "Compare All"}
                </Button>
              )}
              {!baseline && hasEnhancedData && (
                <Button
                  variant="outline"
                  onClick={() => setActiveTab("baseline")}
                  className="gap-2"
                >
                  <Calculator className="h-4 w-4" />
                  Set Baseline
                </Button>
              )}
            </div>
          </CardTitle>
          <CardDescription>
            {hasEnhancedData 
              ? "Compare detailed quotes with savings analysis and implementation timelines"
              : "Review quotes from PEO providers for your applications"
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {hasEnhancedData && showComparison ? (
            isCalculating ? (
              <QuoteComparisonSkeleton />
            ) : (
              <QuoteComparisonMatrix
                quotes={enhancedQuotes}
                metrics={comparisonMetrics}
                onViewDetails={handleViewDetails}
                onRespond={handleRespond}
              />
            )
          ) : (
            <Tabs defaultValue="quotes" value={activeTab} onValueChange={setActiveTab}>
              <TabsList className={hasEnhancedData ? "grid w-full grid-cols-5" : "grid w-full grid-cols-4"}>
                <TabsTrigger value="quotes">All ({quotesToDisplay.length})</TabsTrigger>
                <TabsTrigger value="pending">Pending ({getQuotesByStatus('pending').length})</TabsTrigger>
                <TabsTrigger value="interested">Interested ({getQuotesByStatus('interested').length})</TabsTrigger>
                <TabsTrigger value="accepted">Accepted ({getQuotesByStatus('accepted').length})</TabsTrigger>
                {hasEnhancedData && (
                  <TabsTrigger value="baseline">Baseline Setup</TabsTrigger>
                )}
              </TabsList>
          
              <TabsContent value="quotes" className="mt-6">
                <div className="space-y-4">
                  {quotesToDisplay.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground">
                      No quotes available
                    </div>
                  ) : isMobile ? (
                    <div className="space-y-4">
                      {quotesToDisplay.map((quote) => (
                        <MobileQuoteCard
                          key={quote.id}
                          quote={quote}
                          onAction={handleAction}
                          showEnhancedMetrics={hasEnhancedData}
                          metrics={comparisonMetrics.find(m => m.quote_id === quote.id)}
                        />
                      ))}
                    </div>
                  ) : (
                    quotesToDisplay.map((quote) => (
                      <div key={quote.id} className="border rounded-lg p-4 bg-card">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h3 className="font-medium text-lg">{quote.provider_name || "PEO Provider"}</h3>
                            <p className="text-sm text-muted-foreground">
                              Quote for {quote.company_name || "your application"}
                            </p>
                          </div>
                          <QuoteStatusBadge status={quote.status} />
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div className="space-y-1">
                            <p className="text-sm text-muted-foreground">Monthly Cost</p>
                            <p className="font-medium">
                              {quote.monthly_cost ? formatCurrency(quote.monthly_cost) : "Not specified"}
                            </p>
                          </div>
                          <div className="space-y-1">
                            <p className="text-sm text-muted-foreground">Annual Cost</p>
                            <p className="font-medium">
                              {quote.annual_cost ? formatCurrency(quote.annual_cost) : "Not specified"}
                            </p>
                          </div>
                          <div className="space-y-1">
                            <p className="text-sm text-muted-foreground">Valid Until</p>
                            <div className="flex items-center gap-1">
                              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                              <p className="font-medium">
                                {quote.valid_until ? format(new Date(quote.valid_until), 'MMM d, yyyy') : "Not specified"}
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        {quote.quote_summary && (
                          <div className="mb-4 p-3 bg-muted/50 rounded border">
                            <p className="text-sm">{quote.quote_summary}</p>
                          </div>
                        )}
                        
                        <div className="flex flex-wrap gap-2 mt-4">
                          {quote.quote_file_url && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => window.open(quote.quote_file_url, '_blank')}
                              className="flex items-center gap-2"
                            >
                              <FileText className="h-4 w-4" />
                              View Quote Document
                              <ExternalLink className="h-3 w-3 ml-1" />
                            </Button>
                          )}
                          
                          {quote.status === 'pending' && (
                            <>
                              <Button 
                                size="sm" 
                                variant="default"
                                onClick={() => handleAction(quote, 'interested')}
                              >
                                Mark Interested
                              </Button>
                              <Button 
                                size="sm" 
                                variant="default"
                                onClick={() => handleAction(quote, 'accepted')}
                                className="bg-primary hover:bg-primary/90"
                              >
                                Accept Quote
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => handleAction(quote, 'declined')}
                              >
                                Decline
                              </Button>
                            </>
                          )}
                          
                          {quote.status === 'interested' && (
                            <>
                              <Button 
                                size="sm" 
                                variant="default"
                                onClick={() => handleAction(quote, 'accepted')}
                                className="bg-primary hover:bg-primary/90"
                              >
                                Accept Quote
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => handleAction(quote, 'declined')}
                              >
                                Decline
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>

              <TabsContent value="pending" className="mt-6">
                <div className="space-y-4">
                  {getQuotesByStatus('pending').length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground">
                      No pending quotes
                    </div>
                  ) : (
                    getQuotesByStatus('pending').map((quote) => (
                      <div key={quote.id} className="border rounded-lg p-4 bg-white">
                        {/* ... keep existing code ... */}
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>

              <TabsContent value="interested" className="mt-6">
                <div className="space-y-4">
                  {getQuotesByStatus('interested').length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground">
                      No quotes marked as interested
                    </div>
                  ) : (
                    getQuotesByStatus('interested').map((quote) => (
                      <div key={quote.id} className="border rounded-lg p-4 bg-white">
                        {/* ... keep existing code ... */}
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>

              <TabsContent value="accepted" className="mt-6">
                <div className="space-y-4">
                  {getQuotesByStatus('accepted').length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground">
                      No accepted quotes
                    </div>
                  ) : (
                    getQuotesByStatus('accepted').map((quote) => (
                      <div key={quote.id} className="border rounded-lg p-4 bg-white">
                        {/* ... keep existing code ... */}
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>

              {hasEnhancedData && eligibleApplication && (
                <TabsContent value="baseline" className="mt-6">
                  <BaselineComparisonSetup
                    applicationId={eligibleApplication.id}
                    onBaselineSet={handleBaselineSet}
                    existingBaseline={baseline}
                  />
                </TabsContent>
              )}
            </Tabs>
          )}
        </CardContent>
      </Card>
      
      {/* Tour Guide */}
      <TourGuide
        steps={tourSteps}
        isActive={isTourActive && hasEnhancedData}
        onComplete={completeTour}
        onSkip={skipTour}
      />
    </div>
    </QuoteErrorBoundary>
  );
};

const QuoteStatusBadge = ({ status }: { status: string }) => {
  switch (status) {
    case 'accepted':
      return <Badge className="bg-primary">Accepted</Badge>;
    case 'declined':
      return <Badge variant="destructive">Declined</Badge>;
    case 'interested':
      return <Badge className="bg-secondary">Interested</Badge>;
    default:
      return <Badge variant="outline">Pending</Badge>;
  }
};
